import * as React from 'react'
import { useCallback, useMemo } from 'react'

import type {
  IProgram,
  IProgramInteraction,
  IProgramInteractionId,
} from '@app/domain/program/program.interface'
import {
  IInteractionStatusId,
  ProgramInteractionTypeId,
} from '@app/domain/program/program.interface'
import { theme } from '@app/utils/theme'

import {
  InteractionLabel,
  StyledSelectGroup,
  StyledSelectOption,
} from './styled-select-group'
import { createDaysRemainingLabel, findNearestInteractionId } from './utils'

const DEFAULT_REMOTE_INTERACTION_LABEL = 'Remote Interaction'
const REMOTE_INTERACTION_GROUP_LABEL = 'Remote Interactions'

export type props = {
  initialInteractionCompletedDate: Date
  program: IProgram
  programInteractionHistory: Record<IProgramInteractionId, Date>
  isDisabled?: boolean
}

export const RemoteInteractionSelectOptionsComponent = ({
  initialInteractionCompletedDate,
  program,
  programInteractionHistory,
  isDisabled,
}: props) => {
  const initialInteractionIsCompleted =
    initialInteractionCompletedDate !== undefined
  const nearestRemoteInteractionId = useMemo(() => {
    return findNearestInteractionId(
      initialInteractionCompletedDate,
      program,
      programInteractionHistory,
      ProgramInteractionTypeId.RemoteInteraction,
    )
  }, [initialInteractionCompletedDate, program, programInteractionHistory])

  const getDaysRemainingLabel = useCallback(
    (interaction: IProgramInteraction) => {
      return createDaysRemainingLabel(
        initialInteractionCompletedDate,
        interaction,
        nearestRemoteInteractionId,
        programInteractionHistory,
      )
    },
    [
      initialInteractionCompletedDate,
      nearestRemoteInteractionId,
      programInteractionHistory,
    ],
  )

  const remoteInteractionSelect = useCallback(
    (
      interaction: IProgramInteraction,
      initialInteractionIsCompleted: boolean,
      programInteractionHistory: Record<IProgramInteractionId, Date>,
    ) => {
      const isNearest = interaction.id === nearestRemoteInteractionId
      const interactionIsComplete =
        programInteractionHistory[interaction.id] !== undefined
      const shouldStrikeThrough =
        !initialInteractionIsCompleted || interactionIsComplete

      if (
        interaction.interactionType ===
          ProgramInteractionTypeId.RemoteInteraction &&
        (interaction.status === IInteractionStatusId.Active || isDisabled)
      ) {
        const interactionIsComplete =
          programInteractionHistory[interaction.id] !== undefined

        return (
          <StyledSelectOption
            key={interaction.id}
            value={interaction.id}
            description={
              !initialInteractionIsCompleted
                ? 'Disabled pending initial interaction.'
                : getDaysRemainingLabel(interaction)
            }
            isDisabled={!initialInteractionIsCompleted || interactionIsComplete}
            $isNearest={isNearest}
            $activeColor={isNearest ? theme.colors.active : undefined}
          >
            <InteractionLabel $strikeThrough={shouldStrikeThrough}>
              {interaction.label?.length > 0
                ? interaction.label
                : DEFAULT_REMOTE_INTERACTION_LABEL}
            </InteractionLabel>
          </StyledSelectOption>
        )
      } else {
        return null
      }
    },
    [getDaysRemainingLabel, isDisabled, nearestRemoteInteractionId],
  )

  if (program.interactions[ProgramInteractionTypeId.RemoteInteraction]) {
    return (
      <StyledSelectGroup label={REMOTE_INTERACTION_GROUP_LABEL}>
        {program.interactions[ProgramInteractionTypeId.RemoteInteraction]
          .sort(
            (a, b) =>
              a.daysAfterInitialInteraction - b.daysAfterInitialInteraction,
          )
          .map((interaction) =>
            remoteInteractionSelect(
              interaction,
              initialInteractionIsCompleted,
              programInteractionHistory,
            ),
          )}
      </StyledSelectGroup>
    )
  } else {
    return null
  }
}

export const remoteIteractionSelectOptions = function (
  initialInteractionCompletedDate: Date,
  program: IProgram,
  programInteractionHistory: Record<IProgramInteractionId, Date>,
  isDisabled?: boolean,
) {
  return RemoteInteractionSelectOptionsComponent({
    initialInteractionCompletedDate,
    program,
    programInteractionHistory,
    isDisabled,
  })
}
